import { useEffect, useRef, useState, useMemo } from 'react'
import { Chart, registerables } from 'chart.js'
import { useBudgets, useIncome, useMonthlyReports, useNetWorthHistory, useAccounts, useGoals, useInvestments, useDebts, useQuickSpends, useBills } from '../hooks/useData'
import { useStore } from '../store'
import { useAuth } from '../hooks/useAuth'
import { Card, CardTitle, CyclePicker, fmt, fmt2, fmtPct, colors, fonts, Loading } from '../components/UI'
import { useTheme } from '../lib/theme'
import NetWorthChart from '../components/NetWorthChart'
import { getPayCycle, cyclesForPicker, cycleRangeLabel, billDueDateInMonth, billOccursInMonth } from '../lib/payday'
import Hint from '../components/Hint'

Chart.register(...registerables)

const calendarMonthKey = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}` }

function nav(id) { window.dispatchEvent(new CustomEvent('navigate', { detail: id })) }

// ── Shared tab bar ──────────────────────────────────────────────────────────
function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', margin: '0 0 20px' }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          flex: 1, padding: '12px 4px', border: 'none', background: 'transparent', cursor: 'pointer',
          fontFamily: fonts.body, fontSize: '14px', fontWeight: 600,
          color: t.id === active ? colors.gold : colors.mutedDeep,
          borderBottom: t.id === active ? `2px solid ${colors.gold}` : '2px solid transparent',
          marginBottom: '-1px', transition: 'color 0.15s',
        }}>{t.label}</button>
      ))}
    </div>
  )
}

// ── Drill row (Over time breakdown) ────────────────────────────────────────
function DrillRow({ color, label, value, valueColor, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '13px 0', width: '100%', background: 'none', border: 'none',
      borderBottom: `1px solid ${colors.border}`, cursor: 'pointer', textAlign: 'left',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ width: '3px', height: '16px', background: color, borderRadius: '2px', flexShrink: 0 }} />
        <span style={{ fontSize: '15px', color: colors.muted, fontFamily: fonts.body }}>{label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '15px', color: valueColor || colors.text, fontFamily: fonts.heading, fontWeight: 700 }}>{value}</span>
        <i className="ti ti-chevron-right" style={{ fontSize: '14px', color: colors.mutedDeep }} />
      </div>
    </button>
  )
}

export default function InsightsPage() {
  const { themeId } = useTheme()
  const [tab, setTab] = useState('period')
  const [analysisRange, setAnalysisRange] = useState('month')
  const [analysisYear, setAnalysisYear] = useState(new Date().getFullYear())

  const { profile } = useAuth()
  const payDay = profile?.pay_day
  const shiftWeekends = profile?.pay_day_shift_weekends !== false
  const signupDate = profile?.created_at ? new Date(profile.created_at) : null
  const signupMonthStart = signupDate ? new Date(signupDate.getFullYear(), signupDate.getMonth(), 1) : null

  const allCycles = useMemo(() => cyclesForPicker(payDay, shiftWeekends), [payDay, shiftWeekends])
  const cycles = useMemo(() => {
    if (!signupMonthStart) return allCycles
    return allCycles.filter(c => new Date(c.startIso) >= signupMonthStart)
  }, [allCycles, signupMonthStart])
  const currentCycle = useMemo(() => getPayCycle(payDay, new Date(), shiftWeekends), [payDay, shiftWeekends])
  const [selectedCycleStart, setSelectedCycleStart] = useState(() => currentCycle?.startIso || '')
  const selectedCycle = useMemo(
    () => cycles.find(c => c.startIso === selectedCycleStart) || currentCycle,
    [cycles, selectedCycleStart, currentCycle]
  )
  const periodKey = selectedCycleStart || calendarMonthKey()

  const { budgets, loading, totalBudgeted } = useBudgets(periodKey)
  const { incomeEntries, incomeForMonth, incomeForCycle } = useIncome()
  const { reports } = useMonthlyReports()
  const { history } = useNetWorthHistory()
  const { accounts } = useAccounts()
  const { goals } = useGoals()
  const { totalValue: totalInvestments } = useInvestments()
  const { totalDebt } = useDebts()
  const { categorySpends } = useQuickSpends(selectedCycle?.startIso || '')
  const { bills } = useBills()
  const allQuickSpends = useStore(s => s.quickSpends)
  const allQuickIncome = useStore(s => s.quickIncome)

  const totalIncome = selectedCycle ? incomeForCycle(selectedCycle) : incomeForMonth(calendarMonthKey())
  const quickIncomeThisCycle = allQuickIncome
    .filter(e => e.pay_cycle_start === selectedCycle?.startIso)
    .reduce((s, e) => s + (e.amount || 0), 0)
  const totalCycleIncome = totalIncome + quickIncomeThisCycle

  const totalQuickSpent = allQuickSpends
    .filter(e => e.pay_cycle_start === selectedCycle?.startIso)
    .reduce((s, e) => s + (e.amount || 0), 0)

  // Sum all active bills whose due date falls within the selected cycle window.
  // This works retrospectively - bills added later still count for past cycles.
  const billsThisCycle = selectedCycle ? bills.filter(b => {
    if (!b.is_active && b.is_active !== undefined) return false
    const cycleStart = new Date(selectedCycle.startIso)
    const cycleEnd = new Date(selectedCycle.endIso)
    // Try due date in same month as cycle start, then next month
    let dueDate = new Date(cycleStart.getFullYear(), cycleStart.getMonth(), b.due_day)
    if (dueDate < cycleStart) dueDate = new Date(cycleStart.getFullYear(), cycleStart.getMonth() + 1, b.due_day)
    // Also check if bill occurs in the months covered by this cycle
    const occursStart = billOccursInMonth(b, cycleStart.getFullYear(), cycleStart.getMonth())
    const occursEnd = billOccursInMonth(b, cycleEnd.getFullYear(), cycleEnd.getMonth())
    if (!occursStart && !occursEnd) return false
    return dueDate >= cycleStart && dueDate <= cycleEnd
  }).reduce((s, b) => s + (b.amount || 0), 0) : 0

  const totalSpent = totalQuickSpent + billsThisCycle
  const leftOver = totalCycleIncome - totalSpent
  const unallocatedIncome = totalCycleIncome > 0 ? Math.max(0, totalCycleIncome - totalBudgeted) : 0

  // Merge bill spend into categorySpends by category
  const categorySpendsWithBills = useMemo(() => {
    const merged = { ...categorySpends }
    if (selectedCycle) {
      const cycleStart = new Date(selectedCycle.startIso)
      const cycleEnd = new Date(selectedCycle.endIso)
      bills.forEach(b => {
        if (!b.category || (b.is_active === false)) return
        let dueDate = new Date(cycleStart.getFullYear(), cycleStart.getMonth(), b.due_day)
        if (dueDate < cycleStart) dueDate = new Date(cycleStart.getFullYear(), cycleStart.getMonth() + 1, b.due_day)
        if (dueDate >= cycleStart && dueDate <= cycleEnd) {
          if (!billOccursInMonth(b, cycleStart.getFullYear(), cycleStart.getMonth()) &&
              !billOccursInMonth(b, cycleEnd.getFullYear(), cycleEnd.getMonth())) return
          merged[b.category] = (merged[b.category] || 0) + (b.amount || 0)
        }
      })
    }
    return merged
  }, [categorySpends, bills, selectedCycle])

  const daysLeft = selectedCycle
    ? Math.max(0, Math.ceil((new Date(selectedCycle.endIso) - new Date()) / 86400000))
    : 0

  // ── Analysis tab data ────────────────────────────────────────────────────
  const analysisData = useMemo(() => {
    if (!payDay) return null
    const allAnalysisCycles = cyclesForPicker(payDay, shiftWeekends, {}, new Date())
      .filter(c => !signupMonthStart || new Date(c.startIso) >= signupMonthStart)
      .slice(0, 50)
    const now = new Date()

    let filtered = allAnalysisCycles.filter(c => {
      const d = new Date(c.startIso)
      if (analysisRange === 'month') return d.getFullYear() === analysisYear
      if (analysisRange === 'quarter') return d.getFullYear() === analysisYear
      if (analysisRange === '6months') return d.getFullYear() === analysisYear || (analysisYear === now.getFullYear() && d.getFullYear() === analysisYear - 1)
      if (analysisRange === 'year') return d.getFullYear() === analysisYear
      return true
    }).slice(0, analysisRange === 'month' ? 12 : analysisRange === 'quarter' ? 4 : analysisRange === '6months' ? 6 : 12)

    const cycleResults = filtered.map(cycle => {
      const income = incomeForCycle(cycle) + allQuickIncome.filter(e => e.pay_cycle_start === cycle.startIso).reduce((s, e) => s + (e.amount || 0), 0)
      const quickSpent = allQuickSpends.filter(e => e.pay_cycle_start === cycle.startIso).reduce((s, e) => s + (e.amount || 0), 0)
      const cycleStart = new Date(cycle.startIso)
      const cycleEnd = new Date(cycle.endIso)
      const billsSpent = bills.filter(b => {
        if (!b.is_active && b.is_active !== undefined) return false
        let dueDate = new Date(cycleStart.getFullYear(), cycleStart.getMonth(), b.due_day)
        if (dueDate < cycleStart) dueDate = new Date(cycleStart.getFullYear(), cycleStart.getMonth() + 1, b.due_day)
        if (dueDate < cycleStart || dueDate > cycleEnd) return false
        return billOccursInMonth(b, cycleStart.getFullYear(), cycleStart.getMonth()) ||
               billOccursInMonth(b, cycleEnd.getFullYear(), cycleEnd.getMonth())
      }).reduce((s, b) => s + (b.amount || 0), 0)
      const spent = quickSpent + billsSpent
      return { cycle, income, spent }
    }).reverse()

    const totalInc = cycleResults.reduce((s, d) => s + d.income, 0)
    const totalSp = cycleResults.reduce((s, d) => s + d.spent, 0)
    const surplus = totalInc - totalSp

    // Category breakdown from quickSpends across selected cycles
    const cycleKeys = new Set(filtered.map(c => c.startIso))
    const catMap = {}
    allQuickSpends.filter(e => cycleKeys.has(e.pay_cycle_start)).forEach(e => {
      const cat = e.category || 'Uncategorised'
      catMap[cat] = (catMap[cat] || 0) + (e.amount || 0)
    })
    const catBreakdown = Object.entries(catMap).sort((a, b) => b[1] - a[1])

    return { cycleResults, totalInc, totalSp, surplus, catBreakdown }
  }, [payDay, shiftWeekends, analysisRange, analysisYear, incomeForCycle, allQuickSpends, allQuickIncome, bills, signupMonthStart])

  // ── Chart refs ───────────────────────────────────────────────────────────
  const ringRef = useRef(null); const ringChart = useRef(null)
  const analysisBarRef = useRef(null); const analysisBarChart = useRef(null)
  const cashFlowRef = useRef(null); const cashFlowChart = useRef(null)

  // Spending ring - This period
  useEffect(() => {
    if (tab !== 'period' || !ringRef.current || budgets.length === 0) return
    if (ringChart.current) ringChart.current.destroy()
    const catColors = ['#c9972b','#3db87a','#4a8fe8','#e05252','#7b68ee','#e8b84b','#64b5f6','#81c784']
    const top4 = budgets.slice(0, 4)
    const rest = budgets.slice(4)
    const restTotal = rest.reduce((s, b) => s + b.budgeted, 0)
    const labels = [...top4.map(b => b.category), ...(rest.length > 0 ? ['Other'] : [])]
    const data = [...top4.map(b => b.budgeted), ...(rest.length > 0 ? [restTotal] : [])]
    ringChart.current = new Chart(ringRef.current, {
      type: 'doughnut',
      data: { labels, datasets: [{ data, backgroundColor: catColors.slice(0, labels.length), borderWidth: 0, hoverOffset: 4 }] },
      options: {
        responsive: true, maintainAspectRatio: false, cutout: '70%',
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => {
                const val = ctx.raw
                const total = ctx.dataset.data.reduce((s, v) => s + v, 0)
                return ` ${fmt(val)} (${Math.round(val/total*100)}%)`
              }
            }
          }
        },
        onClick: (evt, elements) => {
          if (elements.length > 0) {
            const idx = elements[0].index
            // handled via state in parent
          }
        }
      }
    })
    return () => { if (ringChart.current) ringChart.current.destroy() }
  }, [tab, budgets, themeId])

  // Analysis bar chart
  useEffect(() => {
    if (tab !== 'analysis' || !analysisBarRef.current || !analysisData) return
    if (analysisBarChart.current) analysisBarChart.current.destroy()
    const { cycleResults } = analysisData
    if (cycleResults.length === 0) return
    const labelFor = (cycle) => {
      const s = new Date(cycle.startIso)
      const e = new Date(cycle.endIso)
      return `${s.toLocaleString('en-GB',{month:'short'})}-${e.toLocaleString('en-GB',{month:'short'})}`
    }
    analysisBarChart.current = new Chart(analysisBarRef.current, {
      type: 'bar',
      data: {
        labels: cycleResults.map(d => labelFor(d.cycle)),
        datasets: [
          { label: 'Income', data: cycleResults.map(d => d.income), backgroundColor: 'rgba(61,184,122,0.75)', borderRadius: 3, borderWidth: 0 },
          { label: 'Spent', data: cycleResults.map(d => d.spent), backgroundColor: 'rgba(201,151,43,0.65)', borderRadius: 3, borderWidth: 0 },
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: true, labels: { color: colors.mutedDeep, font: { size: 11, family: fonts.body }, boxWidth: 10, padding: 14 } } },
        scales: {
          x: { grid: { display: false }, ticks: { color: colors.mutedDeep, font: { size: 10 } } },
          y: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: colors.mutedDeep, font: { size: 11 }, callback: v => v >= 1000 ? '£'+Math.round(v/1000)+'k' : '£'+v } }
        }
      }
    })
    return () => { if (analysisBarChart.current) analysisBarChart.current.destroy() }
  }, [tab, analysisData, analysisRange, analysisYear, themeId])

  // Over time cash flow chart
  useEffect(() => {
    if (tab !== 'overtime' || !cashFlowRef.current) return
    if (cashFlowChart.current) cashFlowChart.current.destroy()
    const chartData = reports.slice(0, 12).reverse()
    if (chartData.length === 0) return
    const labelFor = key => {
      const parts = key.split('-')
      if (parts.length === 3) {
        const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
        const lead = new Date(d.getFullYear(), d.getMonth() + 1, 1)
        return lead.toLocaleString('en-GB', { month: 'short', year: '2-digit' })
      }
      return new Date(key + '-15').toLocaleString('en-GB', { month: 'short', year: '2-digit' })
    }
    cashFlowChart.current = new Chart(cashFlowRef.current, {
      type: 'bar',
      data: {
        labels: chartData.map(r => labelFor(r.month)),
        datasets: [
          { label: 'Income', data: chartData.map(r => r.total_income), backgroundColor: 'rgba(61,184,122,0.7)', borderRadius: 3, borderWidth: 0 },
          { label: 'Spent', data: chartData.map(r => r.total_spent), backgroundColor: 'rgba(201,151,43,0.6)', borderRadius: 3, borderWidth: 0 },
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: true, labels: { color: colors.mutedDeep, font: { size: 11, family: fonts.body }, boxWidth: 10, padding: 14 } } },
        scales: {
          x: { grid: { display: false }, ticks: { color: colors.mutedDeep, font: { size: 10 } } },
          y: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: colors.mutedDeep, font: { size: 11 }, callback: v => v >= 1000 ? '£'+Math.round(v/1000)+'k' : '£'+v } }
        }
      }
    })
    return () => { if (cashFlowChart.current) cashFlowChart.current.destroy() }
  }, [tab, reports, themeId])

  if (loading) return <Loading />

  const catColors = ['#c9972b','#3db87a','#4a8fe8','#e05252','#7b68ee','#e8b84b','#64b5f6','#81c784']
  const savingsTotal = goals.reduce((s, g) => s + (g.current_amount || 0), 0)
  const netWorth = accounts.reduce((s, a) => s + (a.balance || 0), 0) + savingsTotal + totalInvestments - totalDebt
  const accountsByType = Object.entries(accounts.reduce((acc, a) => { acc[a.type] = (acc[a.type] || 0) + a.balance; return acc }, {}))

  return (
    <div style={{ paddingBottom: '32px' }}>

      {/* Header */}
      <div style={{ padding: '18px 20px 0', textAlign: 'center' }}>
        <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '20px', color: colors.text, marginBottom: '8px' }}>Insights</div>
        {tab === 'period' && payDay && (
          <CyclePicker value={selectedCycleStart} onChange={setSelectedCycleStart} cycles={cycles} />
        )}
      </div>

      {/* Tab bar */}
      <div style={{ padding: '16px 16px 0' }}>
        <TabBar
          tabs={[{ id: 'period', label: 'This period' }, { id: 'analysis', label: 'Analysis' }, { id: 'overtime', label: 'Over time' }]}
          active={tab}
          onChange={setTab}
        />
      </div>

      <div style={{ padding: '0 16px' }}>

        {/* ── THIS PERIOD ──────────────────────────────────────────────── */}
        {tab === 'period' && (
          <>
            {/* Hero card */}
            <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', padding: '18px 20px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: colors.green, textTransform: 'uppercase' }}>
                  {selectedCycle ? cycleRangeLabel(selectedCycle).toUpperCase() : 'THIS PERIOD'}
                </div>
                <div style={{ fontSize: '13px', color: colors.mutedDeep, fontFamily: fonts.body }}>{daysLeft > 0 ? `${daysLeft}d left` : 'Ended'}</div>
              </div>

              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: colors.mutedDeep, textTransform: 'uppercase', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>Income vs Spent <Hint title="Income vs Spent" text="Your total income this period minus everything spent (quick spends + bills). A negative figure means you spent more than you earned - this is different from Safe to Spend, which is forward-looking." /></div>
              <div style={{ fontFamily: fonts.heading, fontSize: '32px', fontWeight: 800, color: leftOver >= 0 ? colors.green : colors.red, marginBottom: '16px' }}>
                {leftOver >= 0 ? '+' : ''}{fmt2(leftOver)}
              </div>

              {/* 3-bar visual - side by side */}
              {totalCycleIncome > 0 && (() => {
                const max = Math.max(totalCycleIncome, totalSpent, totalBudgeted, 1)
                const bars = [
                  { label: 'INCOME',   value: totalCycleIncome, color: colors.green },
                  { label: 'SPENT',    value: totalSpent,        color: colors.gold },
                  { label: 'BUDGETED', value: totalBudgeted,     color: colors.blue },
                ]
                return (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '16px' }}>
                    {bars.map(({ label, value, color }) => {
                      const pct = Math.round((value / max) * 100)
                      const barH = Math.max(pct * 1.2, 8)
                      return (
                        <div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                          <div style={{ height: `${barH}px`, width: '100%', background: color, opacity: 0.8, borderRadius: '4px 4px 0 0' }} />
                          <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', color: colors.mutedDeep, textAlign: 'center' }}>{label}</div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color, fontFamily: fonts.heading }}>{fmt(value)}</div>
                        </div>
                      )
                    })}
                  </div>
                )
              })()}

              {unallocatedIncome > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(61,184,122,0.06)', border: '1px solid rgba(61,184,122,0.15)', borderRadius: '10px' }}>
                  <span style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body }}>Unallocated income</span>
                  <span style={{ fontSize: '14px', color: colors.green, fontFamily: fonts.heading, fontWeight: 700 }}>+{fmt2(unallocatedIncome)}</span>
                </div>
              )}
            </div>

            {/* Category list */}
            {budgets.length > 0 && (
              <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', padding: '18px 20px', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', color: colors.mutedDeep, marginBottom: '14px' }}>SPENDING BY CATEGORY</div>
                {budgets.map((b, i) => {
                  const logged = categorySpendsWithBills[b.category] || 0
                  const pct = b.budgeted > 0 ? Math.min((logged / b.budgeted) * 100, 100) : 0
                  const over = logged > b.budgeted
                  return (
                    <div key={b.id} style={{ paddingBottom: '14px', marginBottom: '14px', borderBottom: `1px solid ${colors.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: catColors[i % catColors.length], flexShrink: 0 }} />
                          <span style={{ fontSize: '15px', color: colors.text, fontFamily: fonts.body, fontWeight: 600 }}>{b.category}</span>
                        </div>
                        <span style={{ fontSize: '15px', color: colors.gold, fontFamily: fonts.heading, fontWeight: 700 }}>{fmt2(b.budgeted)}</span>
                      </div>
                      <div style={{ height: '3px', background: 'rgba(255,255,255,0.07)', borderRadius: '2px', marginBottom: '6px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: over ? colors.red : catColors[i % catColors.length], borderRadius: '2px', transition: 'width 0.4s' }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '13px', color: colors.mutedDeep, fontFamily: fonts.body }}>
                          {logged > 0 ? `${fmt2(logged)} spent` : 'Nothing logged yet'}
                        </span>
                        <span style={{ fontSize: '13px', color: over ? colors.red : colors.green, fontFamily: fonts.body, fontWeight: 600 }}>
                          {over ? `${fmt2(logged - b.budgeted)} over` : `${fmt2(b.budgeted - logged)} left`}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Savings rate + days left */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {[
                { label: 'SAVINGS RATE', value: totalCycleIncome > 0 ? `${Math.max(0, Math.round(((totalCycleIncome - totalSpent) / totalCycleIncome) * 100))}%` : '-', color: totalCycleIncome > 0 && totalSpent > totalCycleIncome ? colors.red : colors.green, hint: true },
                { label: 'DAYS LEFT', value: daysLeft > 0 ? `${daysLeft}d` : '-', color: colors.blue },
              ].map(m => (
                <div key={m.label} style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '16px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', color: colors.mutedDeep, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {m.label}
                    {m.hint && <Hint title="Savings Rate" text="The percentage of your income left after all spending this period. Floors at 0% - if you've spent more than you earned it shows 0% in red. Aim for 20% or more." />}
                  </div>
                  <div style={{ fontFamily: fonts.heading, fontSize: '26px', fontWeight: 800, color: m.color }}>{m.value}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── ANALYSIS ─────────────────────────────────────────────────── */}
        {tab === 'analysis' && (
          <>
            {/* Year + range pickers */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
              <select value={analysisYear} onChange={e => setAnalysisYear(Number(e.target.value))} style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '8px', color: colors.text, fontFamily: fonts.heading, fontWeight: 700, fontSize: '16px', padding: '8px 12px', cursor: 'pointer', outline: 'none' }}>
                {[...Array(5)].map((_, i) => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option> })}
              </select>
              <div style={{ display: 'flex', flex: 1, background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                {[{ id: 'month', label: 'Month' }, { id: 'quarter', label: 'Quarter' }, { id: '6months', label: '6 months' }, { id: 'year', label: 'Year' }].map(r => (
                  <button key={r.id} onClick={() => setAnalysisRange(r.id)} style={{
                    flex: 1, padding: '8px 4px', border: 'none', cursor: 'pointer', fontFamily: fonts.body, fontSize: '12px', fontWeight: 600,
                    background: r.id === analysisRange ? colors.gold : 'transparent',
                    color: r.id === analysisRange ? colors.onAccent : colors.mutedDeep,
                  }}>{r.label}</button>
                ))}
              </div>
            </div>

            {analysisData && (
              <>
                {/* Summary */}
                <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', padding: '18px 20px', marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', color: colors.mutedDeep, marginBottom: '4px' }}>
                    {analysisYear} · {analysisRange.toUpperCase()} VIEW
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', color: colours => colors.green, marginBottom: '2px', color: colors.green }}>SURPLUS</div>
                  <div style={{ fontFamily: fonts.heading, fontSize: '28px', fontWeight: 800, color: analysisData.surplus >= 0 ? colors.green : colors.red, marginBottom: '14px' }}>
                    {analysisData.surplus >= 0 ? '+' : ''}{fmt(analysisData.surplus)}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                    {[
                      { label: 'INCOME', value: fmt(analysisData.totalInc), color: colors.green },
                      { label: 'SPENDING', value: fmt(analysisData.totalSp), color: colors.gold },
                    ].map(m => (
                      <div key={m.label} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '10px', padding: '12px' }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', color: colors.mutedDeep, marginBottom: '4px' }}>{m.label}</div>
                        <div style={{ fontFamily: fonts.heading, fontSize: '18px', fontWeight: 700, color: m.color }}>{m.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ position: 'relative', height: '200px' }}>
                    <canvas ref={analysisBarRef} />
                  </div>
                </div>

                {/* Spending by category */}
                {analysisData.catBreakdown.length > 0 && (
                  <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', padding: '18px 20px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: colors.mutedDeep, textTransform: 'uppercase' }}>Spending by Category</div>
                      <div style={{ fontFamily: fonts.heading, fontWeight: 700, color: colors.gold }}>{fmt(analysisData.totalSp)}</div>
                    </div>
                    {analysisData.catBreakdown.map(([cat, amount], i) => {
                      const pct = analysisData.totalSp > 0 ? (amount / analysisData.totalSp) * 100 : 0
                      const txCount = allQuickSpends.filter(e => e.category === cat).length
                      return (
                        <div key={cat} style={{ marginBottom: '14px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: catColors[i % catColors.length], flexShrink: 0 }} />
                              <span style={{ fontSize: '15px', color: colors.text, fontFamily: fonts.body, fontWeight: 600 }}>{cat}</span>
                            </div>
                            <span style={{ fontSize: '15px', color: colors.text, fontFamily: fonts.heading, fontWeight: 700 }}>{fmt(amount)}</span>
                          </div>
                          <div style={{ height: '3px', background: 'rgba(255,255,255,0.07)', borderRadius: '2px', marginBottom: '4px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: catColors[i % catColors.length], borderRadius: '2px' }} />
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '12px', color: colors.mutedDeep }}>{txCount} transaction{txCount !== 1 ? 's' : ''}</span>
                            <span style={{ fontSize: '12px', color: colors.mutedDeep }}>{Math.round(pct)}% of spending</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </>
            )}

            {!analysisData && (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: colors.mutedDeep, fontFamily: fonts.body }}>
                Set your pay day in Settings to see analysis
              </div>
            )}
          </>
        )}

        {/* ── OVER TIME ────────────────────────────────────────────────── */}
        {tab === 'overtime' && (
          <>
            {/* Net worth chart */}
            <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', padding: '18px 20px', marginBottom: '12px' }}>
              <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '16px', color: colors.gold, marginBottom: '14px' }}>Net Worth Over Time</div>
              <NetWorthChart history={history} />
            </div>

            {/* Net worth breakdown */}
            <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', padding: '18px 20px', marginBottom: '12px' }}>
              <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '16px', color: colors.gold, marginBottom: '4px' }}>Net Worth Breakdown</div>
              {accounts.length === 0 ? (
                <div style={{ color: colors.mutedDeep, fontFamily: fonts.body, fontSize: '14px', padding: '16px 0', textAlign: 'center' }}>No accounts added yet</div>
              ) : (
                <>
                  {accountsByType.map(([type, balance]) => {
                    const typeLabel = type === 'credit_card' ? 'Credit cards' : type === 'isa' ? 'ISA' : type.charAt(0).toUpperCase() + type.slice(1)
                    return <DrillRow key={type} color={colors.gold} label={typeLabel} value={fmt(balance)} onClick={() => nav('budget')} />
                  })}
                  {savingsTotal > 0 && <DrillRow color={colors.green} label="Savings" value={fmt(savingsTotal)} onClick={() => nav('savings')} />}
                  {totalInvestments > 0 && <DrillRow color={colors.blue} label="Investments" value={fmt(totalInvestments)} onClick={() => nav('investments')} />}
                  {totalDebt > 0 && <DrillRow color={colors.red} label="Debts" value={`-${fmt(totalDebt)}`} valueColor={colors.red} onClick={() => nav('debt')} />}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0 0' }}>
                    <span style={{ fontSize: '15px', color: colors.muted, fontFamily: fonts.body }}>Net worth</span>
                    <span style={{ fontSize: '16px', color: netWorth < 0 ? colors.red : colors.gold, fontFamily: fonts.heading, fontWeight: 800 }}>{fmt(netWorth)}</span>
                  </div>
                </>
              )}
            </div>

            {/* 12-period cash flow */}
            <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', padding: '18px 20px', marginBottom: '12px' }}>
              <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '16px', color: colors.gold, marginBottom: '14px' }}>12-Period Cash Flow</div>
              {reports.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: colors.mutedDeep, fontFamily: fonts.body, fontSize: '14px' }}>Builds up as you track each period</div>
              ) : (
                <div style={{ position: 'relative', height: '220px' }}><canvas ref={cashFlowRef} /></div>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  )
}
